// src/admin/AdminProducts.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Search, Package, Trash2, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, 'products'));
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try {
      await deleteDoc(doc(db, 'products', id));
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success('Product deleted');
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const toggleActive = async (product) => {
    try {
      const val = !product.isActive;
      await updateDoc(doc(db, 'products', product.id), { isActive: val });
      setProducts((p) => p.map((x) => x.id === product.id ? { ...x, isActive: val } : x));
      toast.success(val ? 'Product activated' : 'Product hidden');
    } catch { toast.error('Update failed'); }
  };

  const filtered = products.filter((p) =>
    !search ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  const liveCount   = products.filter((p) => p.isActive !== false).length;
  const hiddenCount = products.length - liveCount;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">All Products</h2>
          <p className="text-gray-400 font-body text-sm">
            {products.length} total · <span className="text-green-600">{liveCount} live</span> · <span className="text-gray-400">{hiddenCount} hidden</span>
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products by name or category..."
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Package size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 font-body">No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Product', 'Category', 'Price', 'Stock', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-left text-xs font-body font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={product.image} alt={product.name}
                          className="w-12 h-12 rounded-xl object-cover border border-gray-100 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-body font-semibold text-gray-900 text-sm line-clamp-1">{product.name}</p>
                          <p className="text-gray-400 font-body text-xs line-clamp-1">{product.description?.slice(0, 40)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="bg-rose-50 text-rose-600 text-xs font-body font-semibold px-2.5 py-1 rounded-full capitalize">
                        {product.category || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-display font-bold text-gray-900 text-sm">₹{product.price?.toLocaleString('en-IN')}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-body font-semibold px-2.5 py-1 rounded-full ${
                        (product.stock || 0) > 5 ? 'bg-green-100 text-green-700' :
                        (product.stock || 0) > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>{product.stock || 0} left</span>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => toggleActive(product)}
                        className="flex items-center gap-1.5">
                        {product.isActive !== false
                          ? <><ToggleRight size={22} className="text-green-500" /><span className="text-green-600 text-xs font-semibold font-body">Live</span></>
                          : <><ToggleLeft size={22} className="text-gray-400" /><span className="text-gray-400 text-xs font-semibold font-body">Hidden</span></>
                        }
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Link to={`/product/${product.id}`} target="_blank"
                          className="w-8 h-8 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-xl flex items-center justify-center transition-all">
                          <ExternalLink size={14} />
                        </Link>
                        <button onClick={() => handleDelete(product.id, product.name)} disabled={deleting === product.id}
                          className="w-8 h-8 bg-red-50 hover:bg-red-500 text-red-500 hover:text-white rounded-xl flex items-center justify-center transition-all disabled:opacity-50">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
